import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Text,
  Title,
  Group,
  SimpleGrid,
  ThemeIcon,
  Table,
  Badge,
  Loader,
  Center,
  ScrollArea,
  Card,
  Alert,
  Button,
  Progress,
  RingProgress
} from "@mantine/core";
import {
  IconChefHat,
  IconTruckDelivery,
  IconCircleCheck,
  IconRefresh,
  IconAlertCircle,
  IconCalendarTime,
  IconArrowRight
} from "@tabler/icons-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

export default function PenyediaDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/penyedia/dashboard");
      setData(res.data);
    } catch (err) {
      console.error("Gagal memuat dashboard:", err);
      setError("Gagal mengambil data dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) return <Center h="100vh"><Loader size="xl" /></Center>;

  if (error || !data) {
    return (
      <Center h="80vh" style={{ flexDirection: 'column' }}>
         <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mb="md">
            {error || "Data tidak ditemukan."}
         </Alert>
         <Button variant="subtle" onClick={fetchDashboard} leftSection={<IconRefresh size={16}/>}>
            Coba Refresh
         </Button>
      </Center>
    );
  }

  // Safe Destructuring
  const { 
    penyedia_nama = "Mitra Penyedia",
    stats = { porsi_hari_ini: 0, pengiriman_aktif: 0, selesai_bulan_ini: 0 }, 
    prioritas = [], 
    chart = [] 
  } = data;

  // --- ROWS TABEL PRIORITAS ---
  const rows = prioritas.map((item) => {
    const isToday = new Date(item.tanggal).toDateString() === new Date().toDateString();
    return (
      <Table.Tr key={item.id} bg={isToday ? "blue.0" : undefined}>
        <Table.Td>
            <Group gap="xs">
                {isToday && <Badge size="xs" color="red" variant="filled">HARI INI</Badge>}
                <Text fz="sm" fw={500}>
                    {new Date(item.tanggal).toLocaleDateString('id-ID', {day: 'numeric', month: 'short'})}
                </Text>
            </Group>
        </Table.Td>
        <Table.Td>
          <Text fz="sm" fw={600}>{item.sekolah}</Text>
          <Text fz="xs" c="dimmed">{item.menu}</Text>
        </Table.Td>
        <Table.Td>
           <Badge variant="outline" color="gray" size="lg">{item.jumlah}</Badge>
        </Table.Td>
        <Table.Td>
           <Badge 
              color={item.status === 'disiapkan' ? 'yellow' : 'blue'} 
              variant="light"
           >
              {item.status.toUpperCase()}
           </Badge>
        </Table.Td>
        <Table.Td align="right">
             <Button 
                variant="subtle" 
                size="xs" 
                onClick={() => navigate('/penyedia/distribusi')} // Asumsi route manajemen distribusi
             >
                Update
             </Button>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div style={{ padding: 20 }}>
      <Group justify="space-between" mb="lg">
         <div>
            <Title order={2}>Dapur {penyedia_nama}</Title>
            <Text c="dimmed">Pantau produksi dan pengiriman makanan</Text>
         </div>
         <Button variant="white" onClick={fetchDashboard} leftSection={<IconRefresh size={16}/>}>Refresh</Button>
      </Group>

      {/* 1. STATISTIK UTAMA */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        
        {/* Card Target Masak (Penting!) */}
        <Paper withBorder p="md" radius="md" bg="blue.0">
            <Group justify="space-between">
                <div>
                    <Text size="xs" c="blue.9" fw={700} tt="uppercase">Target Masak (Hari Ini)</Text>
                    <Text fw={800} size="2.5rem" c="blue.8">{stats.porsi_hari_ini}</Text>
                    <Text size="xs" c="blue.7">Porsi makanan harus disiapkan</Text>
                </div>
                <ThemeIcon variant="filled" color="blue" size={50} radius="md">
                    <IconChefHat size={30}/>
                </ThemeIcon>
            </Group>
        </Paper>

        {/* Card Status Pengiriman */}
        <Paper withBorder p="md" radius="md">
            <Group justify="space-between">
                <div>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Dalam Proses Kirim</Text>
                    <Text fw={700} size="xl">{stats.pengiriman_aktif} <span style={{fontSize: 14, fontWeight: 400}}>Lokasi</span></Text>
                    <Text size="xs" c="dimmed" mt="xs">Status: Disiapkan / Dikirim</Text>
                </div>
                <ThemeIcon variant="light" color="orange" size="xl" radius="md">
                    <IconTruckDelivery size={24}/>
                </ThemeIcon>
            </Group>
            <Progress value={(stats.pengiriman_aktif > 0) ? 60 : 0} mt="md" color="orange" size="sm" animated />
        </Paper>

        {/* Card Total Selesai */}
        <Paper withBorder p="md" radius="md">
            <Group justify="space-between">
                <div>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Selesai Bulan Ini</Text>
                    <Text fw={700} size="xl">{stats.selesai_bulan_ini} <span style={{fontSize: 14, fontWeight: 400}}>Pengiriman</span></Text>
                    <Text size="xs" c="dimmed" mt="xs">Diterima sekolah dengan baik</Text>
                </div>
                <RingProgress
                    size={70}
                    roundCaps
                    thickness={6}
                    sections={[{ value: 100, color: 'teal' }]}
                    label={<Center><IconCircleCheck size={20} color="teal" /></Center>}
                />
            </Group>
        </Paper>
      </SimpleGrid>

      <Grid>
        {/* 2. TABEL PRIORITAS OPERASIONAL */}
        <Grid.Col span={{ base: 12, md: 7 }}>
            <Card withBorder shadow="sm" radius="md" h="100%">
                <Group justify="space-between" mb="md">
                    <Title order={4}>Jadwal Prioritas</Title>
                    <Badge variant="light" color="red" leftSection={<IconCalendarTime size={12}/>}>
                        Segera Proses
                    </Badge>
                </Group>
                
                <ScrollArea>
                    <Table verticalSpacing="sm" highlightOnHover>
                        <Table.Thead bg="gray.0">
                            <Table.Tr>
                                <Table.Th>Tanggal</Table.Th>
                                <Table.Th>Sekolah & Menu</Table.Th>
                                <Table.Th>Jumlah</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.length > 0 ? rows : (
                                <Table.Tr><Table.Td colSpan={5} align="center" c="dimmed" py="xl">Tidak ada jadwal mendesak.</Table.Td></Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            </Card>
        </Grid.Col>

        {/* 3. CHART PRODUKSI */}
        <Grid.Col span={{ base: 12, md: 5 }}>
            <Card withBorder shadow="sm" radius="md">
                <Title order={4} mb="md">Tren Produksi (7 Hari)</Title>
                <div style={{ height: 280, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chart}>
                            <defs>
                                <linearGradient id="colorPorsi" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#228be6" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#228be6" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis 
                                dataKey="date" 
                                tickFormatter={(val) => new Date(val).toLocaleDateString('id-ID', {day:'numeric'})}
                                tick={{fontSize: 12}}
                            />
                            <YAxis />
                            <RechartsTooltip 
                                labelFormatter={(label) => new Date(label).toLocaleDateString('id-ID', {weekday:'long', day:'numeric', month:'long'})}
                            />
                            <Area 
                                type="monotone" 
                                dataKey="total_porsi" 
                                name="Porsi" 
                                stroke="#228be6" 
                                fillOpacity={1} 
                                fill="url(#colorPorsi)" 
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                <Group justify="center" mt="md">
                    <Text size="xs" c="dimmed" ta="center">
                        Grafik menunjukkan total porsi makanan yang Anda produksi dan distribusikan per hari.
                    </Text>
                </Group>
            </Card>
        </Grid.Col>
      </Grid>
    </div>
  );
}